import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ClientWaitingPill } from "../components/notes/ClientWaiting";
import { useClientWaiting } from "../hooks/useClientWaiting";
import API from "../lib/api";
import { TableSkeleton } from "../components/Skeletons";
import { cacheData, getCachedData } from "../lib/offlineSync";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Plus, MagnifyingGlass, FunnelSimple, FloppyDisk, X, Bookmark, Archive, Trash } from "@phosphor-icons/react";
import { AiBriefButton } from "../components/AiFeatures";
import AddressAutocomplete from "../components/AddressAutocomplete";
import ClientPicker from "../components/ClientPicker";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

import DateInput from "../components/DateInput";
const PROJECT_STATUSES = ["draft", "planned", "in_progress", "on_hold", "awaiting_vendor", "awaiting_approval", "completed", "closed", "cancelled"];
const PROJECT_TYPES = ["maintenance", "refurbishment", "compliance", "planned_works", "emergency", "inspection"];
const PRIORITIES = ["low", "medium", "high", "critical"];
const PRESET_COLORS = ["blue", "emerald", "amber", "red", "purple", "sky", "indigo", "pink"];

function StatusBadge({ status }) {
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium status-${status}`}>{status?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</span>;
}

function PriorityBadge({ priority }) {
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium priority-${priority}`}>{priority?.toUpperCase()}</span>;
}

export default function ProjectsPage() {
  const { user } = useAuth();
  const { byId: waitingById } = useClientWaiting();
  const isAdmin = ["super_admin", "admin"].includes(user?.role);
  const [projects, setProjects] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [workTemplates, setWorkTemplates] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", property_address: "", client: "", client_id: "", client_name: "", client_email: "", client_phone: "", client_company: "", requester_contact_id: "", requester_contact_name: "", requester_contact_color: "", project_type: "maintenance", priority: "medium", budget: "", start_date: "", target_date: "", safety_notes: "" });

  // Filter presets
  const [presets, setPresets] = useState([]);
  const [showSavePreset, setShowSavePreset] = useState(false);
  const [presetForm, setPresetForm] = useState({ name: "", color: "blue" });
  const [activePresetId, setActivePresetId] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [archivedCount, setArchivedCount] = useState(0);
  const [deleteProject, setDeleteProject] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  const fetchProjects = useCallback(async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      if (priorityFilter && priorityFilter !== "all") params.priority = priorityFilter;
      if (typeFilter && typeFilter !== "all") params.project_type = typeFilter;
      if (showArchived) params.include_archived = "true";
      const { data } = await API.get("/projects", { params });
      setProjects(data.projects);
      setTotal(data.total);
      setArchivedCount(data.archived_count || 0);
      if (!search && !statusFilter && !priorityFilter && !typeFilter) {
        cacheData('projects_list', data);
      }
    } catch (err) {
      const cached = await getCachedData('projects_list');
      if (cached) { setProjects(cached.projects || []); setTotal(cached.total || 0); }
    }
    finally { setLoading(false); }
  }, [search, statusFilter, priorityFilter, typeFilter, showArchived]);

  const fetchTemplates = useCallback(async () => {
    try {
      const { data } = await API.get("/work-templates?template_type=project");
      setWorkTemplates(data.templates || []);
    } catch {}
  }, []);

  const fetchPresets = useCallback(async () => {
    try {
      const { data } = await API.get("/filter-presets", { params: { entity_type: "projects" } });
      setPresets(data);
    } catch {}
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);
  useEffect(() => { fetchTemplates(); fetchPresets(); }, [fetchTemplates, fetchPresets]);

  // Auto-open create dialog when arriving with ?new=1 (from QuickCreateMenu)
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setShowCreate(true);
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await API.post("/projects", { ...form, budget: parseFloat(form.budget) || 0 });
      toast.success("Project created");
      setShowCreate(false);
      setForm({ name: "", description: "", property_address: "", client: "", client_id: "", client_name: "", client_email: "", client_phone: "", client_company: "", requester_contact_id: "", requester_contact_name: "", requester_contact_color: "", project_type: "maintenance", priority: "medium", budget: "", start_date: "", target_date: "", safety_notes: "" });
      fetchProjects();
    } catch (err) { toast.error("Failed to create project"); }
  };

  const applyTemplate = (templateId) => {
    if (!templateId || templateId === "__none__") return;
    const tpl = workTemplates.find(t => t.id === templateId);
    if (!tpl || !tpl.fields) return;
    setForm(prev => ({ ...prev, ...tpl.fields }));
    toast.success(`Template "${tpl.name}" applied`);
  };

  const applyPreset = (preset) => {
    const f = preset.filters || {};
    setSearch(f.search || "");
    setStatusFilter(f.status || "all");
    setPriorityFilter(f.priority || "all");
    setTypeFilter(f.project_type || "all");
    setActivePresetId(preset.id);
  };

  const clearPreset = () => {
    setSearch("");
    setStatusFilter("all");
    setPriorityFilter("all");
    setTypeFilter("all");
    setActivePresetId(null);
  };

  const handleSavePreset = async (e) => {
    e.preventDefault();
    try {
      const filters = {};
      if (search) filters.search = search;
      if (statusFilter !== "all") filters.status = statusFilter;
      if (priorityFilter !== "all") filters.priority = priorityFilter;
      if (typeFilter !== "all") filters.project_type = typeFilter;
      await API.post("/filter-presets", {
        name: presetForm.name,
        entity_type: "projects",
        filters,
        color: presetForm.color
      });
      toast.success("Filter preset saved");
      setShowSavePreset(false);
      setPresetForm({ name: "", color: "blue" });
      fetchPresets();
    } catch { toast.error("Failed to save preset"); }
  };

  const handleDeletePreset = async (presetId, e) => {
    e.stopPropagation();
    try {
      await API.delete(`/filter-presets/${presetId}`);
      toast.success("Preset deleted");
      if (activePresetId === presetId) setActivePresetId(null);
      fetchPresets();
    } catch { toast.error("Failed to delete"); }
  };

  const handleRestore = async (projectId) => {
    try {
      await API.put(`/projects/${projectId}/restore`);
      toast.success("Project restored");
      fetchProjects();
    } catch { toast.error("Failed to restore"); }
  };

  const handleDeleteProject = async () => {
    if (!deleteProject) return;
    try {
      await API.delete(`/projects/${deleteProject.id}`);
      toast.success("Project permanently deleted");
      setDeleteProject(null);
      setDeleteConfirmText("");
      fetchProjects();
    } catch { toast.error("Failed to delete project"); }
  };

  const hasActiveFilters = search || statusFilter !== "all" || priorityFilter !== "all" || typeFilter !== "all";

  return (
    <div data-testid="projects-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] tracking-tight">Projects</h1>
          <p className="text-sm text-muted-foreground mt-1">{total} total projects</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowCreate(true)} data-testid="create-project-btn">
            <Plus size={16} className="mr-1" /> New Project
          </Button>
        </div>
      </div>

      {/* Saved Filter Presets */}
      {presets.length > 0 && (
        <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="filter-presets-bar">
          <Bookmark size={14} className="text-muted-foreground shrink-0" />
          {presets.map(p => (
            <button
              key={p.id}
              onClick={() => applyPreset(p)}
              className={`group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${activePresetId === p.id ? `bg-${p.color || "blue"}-100 dark:bg-${p.color || "blue"}-900/30 border-${p.color || "blue"}-300 dark:border-${p.color || "blue"}-700 text-${p.color || "blue"}-700 dark:text-${p.color || "blue"}-300` : "border-border bg-card hover:bg-muted text-muted-foreground hover:text-foreground"}`}
              data-testid={`preset-${p.id}`}
            >
              <span className={`h-1.5 w-1.5 rounded-full bg-${p.color || "blue"}-500`} />
              {p.name}
              <button onClick={(e) => handleDeletePreset(p.id, e)} className="opacity-0 group-hover:opacity-100 transition-opacity ml-0.5" data-testid={`delete-preset-${p.id}`}>
                <X size={10} />
              </button>
            </button>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search projects..." value={search} onChange={(e) => { setSearch(e.target.value); setActivePresetId(null); }} className="pl-9" data-testid="projects-search" />
        </div>
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setActivePresetId(null); }}>
          <SelectTrigger className="w-full sm:w-40" data-testid="projects-status-filter">
            <FunnelSimple size={14} className="mr-1" />
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {PROJECT_STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={v => { setPriorityFilter(v); setActivePresetId(null); }}>
          <SelectTrigger className="w-full sm:w-36" data-testid="projects-priority-filter">
            <SelectValue placeholder="All Priorities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            {PRIORITIES.map(p => <SelectItem key={p} value={p}>{p.toUpperCase()}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={v => { setTypeFilter(v); setActivePresetId(null); }}>
          <SelectTrigger className="w-full sm:w-40" data-testid="projects-type-filter">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {PROJECT_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Filter Actions */}
      <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="filter-actions">
        {hasActiveFilters && (
          <>
            <Button variant="outline" size="sm" onClick={() => setShowSavePreset(true)} data-testid="save-preset-btn">
              <FloppyDisk size={14} className="mr-1" /> Save Preset
            </Button>
            <Button variant="ghost" size="sm" onClick={clearPreset} data-testid="clear-filters-btn">
              <X size={14} className="mr-1" /> Clear Filters
            </Button>
          </>
        )}
        {archivedCount > 0 && (
          <button onClick={() => setShowArchived(!showArchived)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all border ${showArchived ? "bg-muted border-primary/30 text-foreground" : "border-border text-muted-foreground hover:text-foreground hover:bg-muted"}`}
            data-testid="toggle-archived-btn">
            <Archive size={14} />
            {showArchived ? "Hide" : "Show"} Archived ({archivedCount})
          </button>
        )}
      </div>

      {/* Project List */}
      {loading ? (
        <TableSkeleton rows={6} cols={6} />
      ) : projects.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-md">
          <p className="text-muted-foreground mb-2">No projects found</p>
          <Button variant="outline" size="sm" onClick={() => setShowCreate(true)} data-testid="empty-create-btn">Create your first project</Button>
        </div>
      ) : (
        <div className="border border-border rounded-md overflow-hidden">
          <table className="w-full text-sm" data-testid="projects-table">
            <thead>
              <tr className="border-b border-border bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                <th className="text-left px-4 py-3 font-semibold">Project</th>
                <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Type</th>
                <th className="text-left px-4 py-3 font-semibold hidden lg:table-cell">Property</th>
                <th className="text-left px-4 py-3 font-semibold">Priority</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold hidden lg:table-cell">Target</th>
                <th className="text-left px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {projects.map((p) => (
                <tr key={p.id} className={`hover:bg-muted/30 transition-colors ${p.status === "archived" ? "opacity-60" : ""}`}>
                  <td className="px-4 py-3">
                    <Link to={`/projects/${p.id}`} className="font-medium text-foreground hover:text-primary transition-colors" data-testid={`project-link-${p.id}`}>{p.name}</Link>
                    <p className="text-xs text-muted-foreground flex items-center gap-1.5">{p.reference} <ClientWaitingPill item={waitingById[p.id]} testId={`project-client-waiting-${p.id}`} /></p>
                    {p.created_by_name && <p className="text-[10px] text-muted-foreground" data-testid={`project-added-by-${p.id}`}>Added by {p.created_by_name}</p>}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-muted-foreground capitalize">{p.project_type?.replace(/_/g, " ")}</td>
                  <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground text-xs">{p.property_address || "-"}</td>
                  <td className="px-4 py-3"><PriorityBadge priority={p.priority} /></td>
                  <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                  <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground text-xs">{p.target_date || "-"}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      {showArchived && p.status === "archived" && (
                        <Button variant="outline" size="sm" className="text-xs" onClick={(e) => { e.stopPropagation(); handleRestore(p.id); }} data-testid={`restore-project-${p.id}`}>
                          Restore
                        </Button>
                      )}
                      {isAdmin && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={(e) => { e.stopPropagation(); e.preventDefault(); setDeleteProject(p); }} data-testid={`delete-project-${p.id}`}>
                          <Trash size={14} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="create-project-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo]">Create New Project</DialogTitle><DialogDescription>Fill in the details to create a new project.</DialogDescription></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            {workTemplates.length > 0 && (
              <div className="flex items-center gap-2 p-2.5 bg-muted/30 rounded-md border border-dashed border-border" data-testid="project-template-selector">
                <Bookmark size={14} className="text-muted-foreground shrink-0" />
                <span className="text-xs text-muted-foreground shrink-0">Apply Template:</span>
                <Select onValueChange={applyTemplate}>
                  <SelectTrigger className="h-8 text-xs flex-1" data-testid="project-apply-template"><SelectValue placeholder="Select a template to pre-fill..." /></SelectTrigger>
                  <SelectContent>
                    {workTemplates.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex items-center justify-between"><Label>Project Name *</Label>
              <AiBriefButton
                workType="project"
                category={form.project_type}
                propertyAddress={form.property_address}
                onApply={(brief) => setForm(prev => ({
                  ...prev,
                  name: brief.title || prev.name,
                  description: brief.description || prev.description,
                  safety_notes: brief.safety_notes || prev.safety_notes,
                }))}
              />
            </div>
            <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="project-name-input" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={form.project_type} onValueChange={v => setForm({...form, project_type: v})}>
                  <SelectTrigger data-testid="project-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{PROJECT_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={v => setForm({...form, priority: v})}>
                  <SelectTrigger data-testid="project-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p}>{p.toUpperCase()}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Property / Address</Label>
              <AddressAutocomplete
                value={form.property_address}
                onChange={v => setForm({...form, property_address: v})}
                onSelect={(a) => setForm(f => ({
                  ...f,
                  property_address: a.formatted_address || f.property_address,
                  street_address: a.street_address || "",
                  town_city: a.town_city || "",
                  county: a.county || "",
                  postcode: a.postcode || "",
                  latitude: a.latitude || null,
                  longitude: a.longitude || null,
                }))}
                data-testid="project-address-input"
              />
            </div>
            <div><Label>Client / Landlord</Label>
              <div className="mt-1">
                <ClientPicker value={form} onChange={(next) => setForm(next)} testIdPrefix="project-client" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Budget</Label><Input type="number" value={form.budget} onChange={e => setForm({...form, budget: e.target.value})} data-testid="project-budget-input" /></div>
              <div><Label>Start Date</Label><DateInput value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} data-testid="project-start-input" /></div>
              <div><Label>Target Date</Label><DateInput value={form.target_date} onChange={e => setForm({...form, target_date: e.target.value})} data-testid="project-target-input" /></div>
            </div>
            <div><Label>Description / Scope</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3} data-testid="project-desc-input" /></div>
            <div><Label>H&S / Compliance Notes</Label><Textarea value={form.safety_notes} onChange={e => setForm({...form, safety_notes: e.target.value})} rows={2} data-testid="project-safety-input" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button type="submit" data-testid="submit-project-btn">Create Project</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Save Preset Dialog */}
      <Dialog open={showSavePreset} onOpenChange={setShowSavePreset}>
        <DialogContent data-testid="save-preset-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo]">Save Filter Preset</DialogTitle><DialogDescription>Save your current filters for quick access.</DialogDescription></DialogHeader>
          <form onSubmit={handleSavePreset} className="space-y-4">
            <div><Label>Preset Name *</Label><Input value={presetForm.name} onChange={e => setPresetForm({...presetForm, name: e.target.value})} required placeholder="e.g. High Priority Active" data-testid="preset-name-input" /></div>
            <div>
              <Label>Color</Label>
              <div className="flex gap-2 mt-1">
                {PRESET_COLORS.map(c => (
                  <button key={c} type="button" onClick={() => setPresetForm({...presetForm, color: c})}
                    className={`h-6 w-6 rounded-full bg-${c}-500 transition-all ${presetForm.color === c ? "ring-2 ring-offset-2 ring-offset-background ring-primary scale-110" : "hover:scale-105"}`}
                    data-testid={`preset-color-${c}`} />
                ))}
              </div>
            </div>
            <div className="border border-border rounded-md p-3 bg-muted/30">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Active Filters</p>
              <div className="flex flex-wrap gap-1.5">
                {search && <Badge variant="secondary" className="text-[10px]">Search: {search}</Badge>}
                {statusFilter !== "all" && <Badge variant="secondary" className="text-[10px]">Status: {statusFilter.replace(/_/g, " ")}</Badge>}
                {priorityFilter !== "all" && <Badge variant="secondary" className="text-[10px]">Priority: {priorityFilter}</Badge>}
                {typeFilter !== "all" && <Badge variant="secondary" className="text-[10px]">Type: {typeFilter.replace(/_/g, " ")}</Badge>}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowSavePreset(false)}>Cancel</Button>
              <Button type="submit" data-testid="confirm-save-preset-btn">Save Preset</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Project Confirmation Dialog */}
      <Dialog open={!!deleteProject} onOpenChange={(open) => { if (!open) { setDeleteProject(null); setDeleteConfirmText(""); } }}>
        <DialogContent className="max-w-md" data-testid="delete-project-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo] text-destructive">Delete Project Permanently</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">
              This action cannot be undone. This will permanently remove <span className="font-semibold text-foreground">{deleteProject?.name}</span> and all associated data:
            </p>
            <ul className="text-xs text-muted-foreground list-disc pl-5 space-y-1">
              <li>All tasks and subtasks</li>
              <li>All works orders</li>
              <li>All compliance documents</li>
              <li>All project messages</li>
              <li>All cost tracking and milestones</li>
            </ul>
            <div className="border border-border rounded-md p-3 bg-muted/30 space-y-1 text-xs">
              <p><span className="font-semibold">Project:</span> {deleteProject?.name}</p>
              <p><span className="font-semibold">Reference:</span> {deleteProject?.reference}</p>
              <p><span className="font-semibold">Status:</span> {deleteProject?.status?.replace(/_/g, " ")}</p>
            </div>
            <div>
              <Label className="text-xs">Type <span className="font-bold text-destructive">DELETE</span> to confirm</Label>
              <Input value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)} placeholder="Type DELETE to confirm" data-testid="delete-project-confirm-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteProject(null); setDeleteConfirmText(""); }}>Cancel</Button>
            <Button variant="destructive" disabled={deleteConfirmText !== "DELETE"} onClick={handleDeleteProject} data-testid="confirm-delete-project-btn">Delete Permanently</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
